import { useState, type ReactNode } from "react";
import { apiService } from "@/services/api.service";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Bot, Keyboard, Mic, NotebookPen, PenLine, TriangleAlert, User } from "lucide-react";
import {
  formatNotepadLineTime,
  isAppPrompt,
  notepadLineDate,
  notepadSourceLabel,
  splitNotepadContent,
} from "@/lib/notepad-transcript";
import type { NotepadMessage, Session } from "./types";

const BITE_TYPE_LABELS = new Map<string, string>([
  ["INSTRUCTIONAL", "Instructional"],
  ["PRACTICE", "Practice"],
]);

const sourceIcon = (source: string | null | undefined): ReactNode => {
  if (source === "voice") return <Mic className="h-3 w-3" aria-hidden="true" />;
  if (source === "typed") return <Keyboard className="h-3 w-3" aria-hidden="true" />;
  return null;
};

const prettyCommand = (raw: string): string => {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
};

const Notice = ({ children, warning = false }: { children: ReactNode; warning?: boolean }) => (
  <div
    role={warning ? "alert" : "status"}
    className={`flex items-center justify-center gap-2 rounded-lg border border-dashed px-3 py-6 text-center text-xs ${
      warning ? "border-amber-300 bg-amber-50 text-amber-900" : "text-muted-foreground"
    }`}
  >
    {warning && <TriangleAlert className="h-4 w-4 shrink-0" aria-hidden="true" />}
    <span>{children}</span>
  </div>
);

const TutorContent = ({ content }: { content: string }) => (
  <div className="space-y-1.5 text-xs leading-relaxed">
    {splitNotepadContent(content).map((segment, i) =>
      segment.kind === "text" ? (
        <p key={i} className="whitespace-pre-wrap break-words">
          {segment.text}
        </p>
      ) : (
        <details key={i} className="group">
          <summary className="inline-flex cursor-pointer list-none items-center gap-1 rounded-md border border-border/70 bg-background/70 px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground hover:bg-background">
            <PenLine className="h-3 w-3" aria-hidden="true" />
            canvas: {segment.name}
          </summary>
          <pre className="mt-1 max-h-60 overflow-auto rounded-md bg-background p-2 text-[11px] leading-snug">
            {prettyCommand(segment.raw)}
          </pre>
        </details>
      ),
    )}
  </div>
);

const LineMeta = ({ line, align }: { line: NotepadMessage; align: "start" | "end" }) => {
  const date = notepadLineDate(line.ts_ms);
  const time = formatNotepadLineTime(line.ts_ms);
  const label = line.role === "user" ? (isAppPrompt(line) ? "Sent by the app" : notepadSourceLabel(line)) : null;
  const icon = sourceIcon(line.source);
  if (!time && !label) return null;
  return (
    <span
      className={`mt-1 flex items-center gap-1 text-xs text-muted-foreground ${
        align === "end" ? "justify-end" : "justify-start"
      }`}
    >
      {icon}
      {label}
      {label && time && <span aria-hidden="true">·</span>}
      {date && time && <time dateTime={date.toISOString()}>{time}</time>}
    </span>
  );
};

const NotepadLine = ({ line }: { line: NotepadMessage }) => {
  // The app's kickoff and nudges sit in the student's role; they must not
  // read as something the student said.
  if (isAppPrompt(line)) {
    return (
      <div className="flex flex-col items-center">
        <div className="max-w-[85%] rounded-lg border border-dashed px-3 py-1.5 text-center text-xs text-muted-foreground">
          <p className="whitespace-pre-wrap break-words">{line.content}</p>
        </div>
        <LineMeta line={line} align="start" />
      </div>
    );
  }

  const isUser = line.role === "user";
  return (
    <div className={`flex gap-2 ${isUser ? "flex-row-reverse" : "flex-row"}`}>
      <Avatar className="size-7 shrink-0 mt-0.5">
        <AvatarFallback className="bg-muted text-xs overflow-hidden">
          {isUser ? <User className="h-3.5 w-3.5" /> : <Bot className="h-3.5 w-3.5" />}
        </AvatarFallback>
      </Avatar>
      <div className={`flex max-w-[75%] flex-col ${isUser ? "items-end" : "items-start"}`}>
        <div
          className={`overflow-hidden px-3 py-2 ${
            isUser
              ? "bg-primary text-primary-foreground rounded-2xl rounded-br-md"
              : "bg-muted rounded-2xl rounded-bl-md"
          }`}
        >
          {isUser ? (
            <p className="whitespace-pre-wrap break-words text-xs leading-relaxed">{line.content}</p>
          ) : (
            <TutorContent content={line.content} />
          )}
        </div>
        <LineMeta line={line} align={isUser ? "end" : "start"} />
      </div>
    </div>
  );
};

type DeferredState =
  | { status: "idle" | "loading" | "failed" }
  | { status: "loaded"; lines: NotepadMessage[] };

/** A conversation the studio left out of the page to bound its size; read on request. */
const DeferredConversation = ({ userId, s3Key }: { userId: string; s3Key: string }) => {
  const [state, setState] = useState<DeferredState>({ status: "idle" });

  const load = async () => {
    setState({ status: "loading" });
    try {
      const history = await apiService.getNotepadHistory(userId, s3Key);
      setState(
        history.notepad_messages
          ? { status: "loaded", lines: history.notepad_messages }
          : { status: "failed" },
      );
    } catch {
      setState({ status: "failed" });
    }
  };

  if (state.status === "loaded") {
    return state.lines.length === 0 ? (
      <Notice>No conversation was saved for this notepad bite. Nobody spoke or typed, or it could not be stored.</Notice>
    ) : (
      <>
        {state.lines.map((line, idx) => (
          <NotepadLine key={`${line.turn_id ?? "line"}-${idx}`} line={line} />
        ))}
      </>
    );
  }
  return (
    <Notice warning={state.status === "failed"}>
      <span className="flex flex-col items-center gap-2">
        {state.status === "failed"
          ? "This conversation could not be loaded. It was stored in S3 and could not be read back."
          : "This conversation is large, so it was not loaded with the list."}
        <Button size="sm" variant="outline" onClick={load} disabled={state.status === "loading"}>
          {state.status === "loading" ? "Loading…" : state.status === "failed" ? "Try again" : "Load conversation"}
        </Button>
      </span>
    </Notice>
  );
};

/** A notepad bite's conversation: the student's lines, the app's prompts, and the tutor's replies with their canvas commands. */
export const NotepadMessageList = ({ session, userId }: { session: Session; userId?: string }) => {
  const biteLabel = session.bite_type ? BITE_TYPE_LABELS.get(session.bite_type) : undefined;
  const lines = session.notepad_messages ?? [];

  return (
    <div className="space-y-2">
      <div className="flex justify-center">
        <Badge variant="outline" className="gap-1">
          <NotebookPen aria-hidden="true" />
          Notepad bite{biteLabel ? ` · ${biteLabel}` : ""}
        </Badge>
      </div>
      {session.notepad_messages_deferred && session.notepad_messages_s3_key && userId ? (
        <DeferredConversation key={session.session_key} userId={userId} s3Key={session.notepad_messages_s3_key} />
      ) : session.notepad_messages_unavailable || session.notepad_messages_deferred ? (
        <Notice warning>
          This conversation could not be loaded. It was stored in S3 and could not be read back, or its stored copy is not valid.
        </Notice>
      ) : lines.length === 0 ? (
        <Notice>No conversation was saved for this notepad bite. Nobody spoke or typed, or it could not be stored.</Notice>
      ) : (
        lines.map((line, idx) => <NotepadLine key={`${line.turn_id ?? "line"}-${idx}`} line={line} />)
      )}
    </div>
  );
};
